import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import Modal from "@/components/Modal";
import { useForm } from "react-hook-form";
import FormDatePicker from "@/components/FormDatePicker";
import { updateProject } from "@/api/projectsApi";
import { ProjectRecord } from "@/types";
import SVGEditNote from "@/assets/icons/SVGEditNote";
import { StyledIconButton } from "@/components/styled";
import { getErrorMessage } from "@/utils/getErrorMessage";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Seed the picker with the same calendar day the project page shows.
 *
 * The stored value is an instant, and the page renders it in the program's timezone. Reading it
 * back in the browser's timezone would open the picker on a different day for anyone outside
 * Pacific, so an admin "correcting" the date would silently shift it by one.
 */
const toPickerValue = (dateString: string | null) =>
  dateString
    ? dayjs(dateString).tz("America/Los_Angeles").format("YYYY-MM-DDTHH:mm:ss")
    : null;

type Props = {
  projectId: ProjectRecord["id"];
  currentDate: string | null;
  onSuccess?: () => void;
};

type FormData = {
  operationalizedDate: string | null;
};

const EditOperationalizedDateModal = ({
  projectId,
  currentDate,
  onSuccess,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      operationalizedDate: toPickerValue(currentDate),
    },
  });

  const handleOpen = () => {
    reset({
      operationalizedDate: toPickerValue(currentDate),
    });
    setOpen(true);
    setError(null);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setOpen(false);
      setError(null);
      reset();
    }
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      if (data.operationalizedDate) {
        formData.append(
          "operationalizedDate",
          dayjs(data.operationalizedDate).toISOString()
        );
      }

      await updateProject(projectId, formData);

      setOpen(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: unknown) {
      setError(
        getErrorMessage(err, "Failed to update operationalized date. Please try again.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <StyledIconButton onClick={handleOpen}>
        <SVGEditNote />
      </StyledIconButton>

      <Modal
        open={open}
        onClose={handleClose}
        title="Edit Operationalized Date"
        boxSx={{
          maxWidth: 500,
          width: "100%",
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: "textLightGray3" }}>
              Update the operationalized date for this project.
            </Typography>

            <FormDatePicker
              name="operationalizedDate"
              control={control}
              label="Operationalized Date"
              controlProps={{
                rules: {
                  required: "Operationalized date is required",
                },
              }}
            />

            {error && (
              <Typography
                variant="body2"
                sx={{ color: "error.main", mt: 2, fontSize: "14px" }}
              >
                {error}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              mt: 3,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isSubmitting}
              sx={{
                borderColor: "#D0D5DD",
                color: "#344054",
                "&:hover": {
                  borderColor: "#D0D5DD",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{
                backgroundColor: "#0076CE",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#0056a3",
                },
                "&:disabled": {
                  backgroundColor: "#B0B0B0",
                  color: "#fff",
                },
              }}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </Box>
        </form>
      </Modal>
    </>
  );
};

export default EditOperationalizedDateModal;
